import { Alert as AntdAlert, Checkbox, Tag as AntdTag, Typography as AntdTypography, theme } from "../../../../../shared/frontend/ui";
import { Alert as KitAlert, StatusText, Tag as KitTag, Typography as KitTypography } from "../../../../../shared/frontend/ui";
import { RepeatableBlock } from "./RepeatableBlocks";
import React from "react";
import { borderRadius as designBorderRadius, spacing, spacing as designSpacing } from "../app/design-system";
import { formatMoney } from "../app/units/format";
import type { UnitsSettings } from "../app/units/units";
import {
  activeOptionGroupItems,
  type OptionGroupSelectionState,
  OPTION_GROUP_KIND_CHOICE,
  OPTION_GROUP_MODE_INCLUDED,
  optionGroupBundleFactor,
  optionGroupEffectivePrice,
  optionGroupListTotal,
  type ContractOptionGroupDto,
  type ContractPackageTemplateOptionGroupDto
} from "../app/utils/contractOptionGroups";

// Im öffentlichen Abschlussformular bleiben die Antd-Bausteine (Widget-Design über dessen ConfigProvider),
// in der internen Vertragsanlage die Kit-Bausteine wie überall sonst.
const widgetUi = { Alert: AntdAlert, Tag: AntdTag, Typography: AntdTypography };
const appUi = { Alert: KitAlert, Tag: KitTag, Typography: KitTypography };

type ContractOptionGroupSelectorProps = {
  /** Verwendungen aus der Paketvorlage, in ihrer Reihenfolge. */
  usages: ContractPackageTemplateOptionGroupDto[];
  /** Gruppen-Stammdaten (mandantenweit). */
  groups: ContractOptionGroupDto[];
  value: OptionGroupSelectionState;
  onChange: (next: OptionGroupSelectionState) => void;
  /** Listenpreis eines Bausteins, wenn die Gruppe keinen eigenen Preis führt. */
  defaultPriceOf: (contractTemplateId: string) => number | null | undefined;
  /** Öffentliche Widgets haben keine Mandanten-Einstellungen; dann gelten die Standardwerte. */
  units?: UnitsSettings | null;
  text: (key: string) => string;
  readOnly?: boolean;
  /** Öffentliches Abschlussformular: Antd-Bausteine im Widget-Design statt Kit. */
  publicWidget?: boolean;
};

/**
 * Auswahl der Zubuchoptionen — dieselbe Komponente für die interne Vertragsanlage und das
 * Abschlussformular. Die Regeln (Bündel vollständig, Min/Max bei Auswahl, fest enthaltene Gruppen)
 * werden hier nur sichtbar gemacht; verbindlich prüft sie der Server bei der Vertragsanlage.
 */
export function ContractOptionGroupSelector({
  usages,
  groups,
  value,
  onChange,
  defaultPriceOf,
  units,
  text,
  readOnly,
  publicWidget = false
}: ContractOptionGroupSelectorProps) {
  const { token } = theme.useToken();
  const { Alert, Tag, Typography } = publicWidget ? widgetUi : appUi;

  const groupById = React.useMemo(() => new Map(groups.map((g) => [String(g.id), g])), [groups]);
  const visibleUsages = React.useMemo(
    () =>
      [...(usages || [])]
        .filter((u) => u.isActive !== false && groupById.get(String(u.contractOptionGroupId))?.isActive !== false)
        .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0)),
    [usages, groupById]
  );

  if (!visibleUsages.length) return null;

  const setSelection = (groupId: string, ids: string[]) => {
    onChange({ ...value, [groupId]: ids });
  };

  return (
    <div style={publicWidget ? { marginTop: designSpacing.controlCompact, display: "grid", gap: designSpacing.controlGap } : { display: "grid", gap: spacing.md }}>
      {visibleUsages.map((usage) => {
        const groupId = String(usage.contractOptionGroupId);
        const group = groupById.get(groupId);
        if (!group) return null;

        const items = activeOptionGroupItems(group);
        const isIncluded = Number(usage.selectionMode) === OPTION_GROUP_MODE_INCLUDED;
        const isChoice = Number(group.selectionKind) === OPTION_GROUP_KIND_CHOICE;
        const selected = isIncluded ? items.map((i) => String(i.contractTemplateId)) : (value[groupId] || []);
        const selectedSet = new Set(selected.map(String));

        const listTotal = optionGroupListTotal(group, selected, defaultPriceOf);
        const factor = optionGroupBundleFactor(group, usage.priceValueOverride, selected.length, listTotal);
        const effective = optionGroupEffectivePrice(listTotal, factor);
        const hasSaving = selected.length > 0 && factor < 1;

        const maxReached =
          isChoice && group.maxSelect != null && selected.length >= Number(group.maxSelect);
        const minMissing =
          isChoice && !isIncluded && group.minSelect != null && selected.length > 0 && selected.length < Number(group.minSelect);

        // Interner Abschluss: hier zählt der Name der Gruppe. Die Vermarktung gehört zum
        // Erlebnisformular und wäre am Tresen eher irreführend als hilfreich.
        const headerExtra = isIncluded ? (
          publicWidget ? <Tag color="default">{text("Included in the package")}</Tag> : <StatusText>{text("Included in the package")}</StatusText>
        ) : isChoice ? null : (
          <Checkbox disabled={readOnly} checked={selected.length > 0} onChange={({ target: { checked } }) => setSelection(groupId, checked ? items.map((i) => String(i.contractTemplateId)) : [])} />
        );
        const content = (
          <>
            <div style={{ display: "grid", gap: spacing.xs }}>
              {items.map((item) => {
                const itemId = String(item.contractTemplateId);
                const itemPrice = Number(item.value ?? defaultPriceOf(itemId) ?? 0);
                const checked = selectedSet.has(itemId);
                const disabled = readOnly || isIncluded || !isChoice || (!checked && maxReached);
                return (
                  <div key={itemId} style={{ display: "flex", gap: spacing.sm, justifyContent: "space-between", alignItems: "center" }}>
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...selected.map(String), itemId]
                          : selected.map(String).filter((x) => x !== itemId);
                        setSelection(groupId, next);
                      }}
                    >
                      {item.contractTemplateName || itemId}
                    </Checkbox>
                    <Typography.Text type="secondary">{formatMoney(itemPrice, units)}</Typography.Text>
                  </div>
                );
              })}
            </div>

            {!isChoice && !isIncluded ? (
              <div style={{ marginTop: designSpacing.controlCompact }}>
                <Typography.Text type="secondary">{text("Book all or nothing")}</Typography.Text>
              </div>
            ) : null}

            {isChoice && !isIncluded && (group.minSelect != null || group.maxSelect != null) ? (
              <div style={{ marginTop: designSpacing.controlCompact }}>
                <Typography.Text type="secondary">
                  {group.minSelect != null ? text("Select at least {0}").replace("{0}", String(group.minSelect)) : ""}
                  {group.minSelect != null && group.maxSelect != null ? " · " : ""}
                  {group.maxSelect != null ? text("Select at most {0}").replace("{0}", String(group.maxSelect)) : ""}
                </Typography.Text>
              </div>
            ) : null}

            {selected.length > 0 ? (
              <div style={{ marginTop: designSpacing.controlGap, display: "flex", gap: spacing.sm, justifyContent: "space-between" }}>
                <Typography.Text type="secondary">{text("Total price")}</Typography.Text>
                <Typography.Text style={{ textAlign: "right" }}>
                  {formatMoney(effective, units)}
                  {hasSaving ? ` · ${text("instead of {0}").replace("{0}", formatMoney(listTotal, units))}` : ""}
                </Typography.Text>
              </div>
            ) : null}

            {minMissing ? (
              <Alert
                type="warning"
                style={{ marginTop: designSpacing.controlGap }}
                title={text("Select at least {0}").replace("{0}", String(group.minSelect))}
              />
            ) : null}
          </>
        );

        // Interne Vertragsanlage: dieselbe Kit-Karte wie die Bausteine darüber.
        if (!publicWidget) {
          return (
            <RepeatableBlock key={groupId} title={group.name} removeLabel={text("Remove")} extra={headerExtra}>
              {content}
            </RepeatableBlock>
          );
        }
        return (
          <div
            key={groupId}
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: designBorderRadius.lg,
              padding: spacing.md,
              background: token.colorFillQuaternary
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing.md, marginBottom: designSpacing.controlGap }}>
              <b>{group.name}</b>
              {headerExtra}
            </div>
            {content}
          </div>
        );
      })}
    </div>
  );
}
